import {Monster, MonsterQuery, TraitSpellLine} from '../../generated/types';
import React from 'react';
import {uppercaseFirstLetter} from '../utils';
import {Markdown, MarkdownInline} from '../utils/md';
import {MonsterTrait} from '../../pages/monsters';
import {
	CrToXp,
	getMonsterShortName,
	splitAvgFormula,
	toMonsterAcNote,
	toMonsterGear,
	toMonsterImmune,
	toMonsterInitiative,
	toMonsterLanguages,
	toMonsterResist,
	toMonsterSenses,
	toMonsterSkills,
	toMonsterSpeed,
} from '../render-helpers';
import {MonsterBlockTable} from '../monster-block-table';
import {DicePlugin} from '../utils/md/plugins';

import markdownCss from '../utils/md/markdown.module.css';
import css from './monster-view.module.css';
import {EditHoc} from '../edit-hoc';

type Props = {
	monster: NonNullable<MonsterQuery['monster']>;
	/** что встаёт рядом с характеристиками: на странице чудовища это токен, в книге там пусто */
	aside?: React.ReactNode;
	/** что идёт под блоком: другие названия и источник на странице чудовища */
	footer?: React.ReactNode;
};

export const MonsterStatblock: React.FC<Props> = ({monster, aside, footer}) => {
	type FeatureKey = keyof Pick<Monster, 'features' | 'actions' | 'bonusActions' | 'reactions' | 'legendaryActions'>;
	const featuresTypes: Record<FeatureKey, string> = {
		features: 'Особенности',
		actions: 'Действия',
		bonusActions: 'Бонусные действия',
		reactions: 'Ответные действия',
		legendaryActions: 'Легендарные действия',
	};

	// ПЗ не всегда содержат формулу: бывает просто текст вроде «половина максимума ПЗ призвавшего»
	const [, hpFormula] = splitAvgFormula(monster.hp);

	return (
		<div className={css.wrapper}>
			<EditHoc
				load={() => import('../../pages/monsters/edit-buttons').then((m) => m.EditMonster)}
				editProps={{id: monster.id}}
			>
				<div className={markdownCss.margin}>
					<div className={markdownCss.balanced}>
						<div className={markdownCss.indent}>
							<p>
								<b>КБ</b> {monster.ac}
								{monster.acNote && (
									<>
										{' '}
										<MarkdownInline text={toMonsterAcNote(monster.acNote)} />
									</>
								)}
							</p>
							<p>
								<b>Инициатива</b>{' '}
								{toMonsterInitiative(monster.dex, monster.cr, monster.initiativeProfBonus)}
							</p>
							<p>
								<b>ПЗ</b>{' '}
								{hpFormula ? <DicePlugin args={[hpFormula, monster.hp]} name="dice" /> : monster.hp}
							</p>
							<p>
								<b>Скорость</b> {toMonsterSpeed(monster.speed)}
							</p>
						</div>
					</div>
				</div>

				<MonsterBlockTable
					params={{
						str: monster.str,
						dex: monster.dex,
						con: monster.con,
						int: monster.int,
						wis: monster.wis,
						cha: monster.cha,
					}}
					saves={monster.saves}
				/>

				{aside}

				<div className={markdownCss.margin}>
					<div className={markdownCss.balanced}>
						<div className={markdownCss.indent}>
							{monster.skills && (
								<p>
									<b>Навыки</b> <MarkdownInline text={toMonsterSkills(monster.skills)} />
								</p>
							)}
							{monster.vulnerable && (
								<p>
									<b>Уязвимость к урону</b> <MarkdownInline text={toMonsterResist(monster.vulnerable)} />
								</p>
							)}
							{monster.resist && (
								<p>
									<b>Устойчивости</b> <MarkdownInline text={toMonsterResist(monster.resist)} />
								</p>
							)}
							{(monster.immune || monster.conditionImmune) && (
								<p>
									<b>Невосприимчивости</b> <MarkdownInline text={toMonsterImmune(monster.immune, monster.conditionImmune)} />
								</p>
							)}
							{monster.gear && toMonsterGear(monster.gear)}
							{monster.mount && (
								<Markdown text={`*Скакун* есть в разделе {@item ${monster.mount.id}, предметов}`} />
							)}
							<p>
								<b>Восприятие</b> <MarkdownInline text={toMonsterSenses(monster.passive, monster.senses)} />
							</p>
							<p>
								<b>Языки</b>{' '}
								{monster.languages || monster.understandLanguages || monster.telepathy
									? `${toMonsterLanguages(
											monster.languages,
											monster.understandLanguages,
											monster.telepathy,
									  )}`
									: '—'}
							</p>
							<p>
								<b>КО</b> {CrToXp(monster.cr, monster.xpLair, monster.proficiencyNote)}
							</p>
						</div>
					</div>
				</div>
			</EditHoc>

			{Object.keys(featuresTypes).map((type) => {
				const ruName = featuresTypes[type as keyof typeof featuresTypes];

				if (monster[type as FeatureKey] && monster[type as FeatureKey]!.length > 0) {
					return (
						<React.Fragment key={type}>
							<h2>{ruName}</h2>
							{type === 'legendaryActions' && (
								<p>
									<i>
										Использования легендарных действий: {monster.legendaryActionsCount}
										{monster.legendaryActionsLairCount &&
											` (${monster.legendaryActionsLairCount} в Логове)`}
										. Сразу после хода другого существа{' '}
										{getMonsterShortName(monster.title, monster.shortTitle)} может потратить одно
										использование, чтобы совершить одно из представленных ниже действий.{' '}
										{uppercaseFirstLetter(getMonsterShortName(monster.title || ''))} восстанавливает
										все потраченные использования в начале каждого своего хода.
									</i>
								</p>
							)}
							{type === 'features' && monster.mount && (
								<MonsterTrait
									title={'Скакун'}
									description={`Это чудовище может быть {@item ${monster.mount.id}, скакуном}.`}
									monster={monster}
								/>
							)}
							<div>
								{monster[type as FeatureKey]!.map((feature) => (
									<MonsterTrait
										id={feature.id}
										key={feature.id}
										title={`${feature.title}`}
										description={feature.description!}
										monster={monster}
										availSpells={feature.availSpells as TraitSpellLine[]}
									/>
								))}
							</div>
						</React.Fragment>
					);
				}

				return null;
			})}

			{monster.lairTraits && monster.monsterLair && monster.monsterLair.description && (
				<>
					<h2>{monster.monsterLair.title}</h2>
					<Markdown
						text={monster.monsterLair.description}
						payload={{lairTraits: monster.lairTraits, monster}}
					/>
				</>
			)}

			{footer}
		</div>
	);
};
